import type { DetailsHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cx, sizeClass, type Hue } from './shared';

export interface AccordionProps extends HTMLAttributes<HTMLDivElement> {
  boxed?: boolean;
  flush?: boolean;
  size?: 'sm' | 'base' | 'lg';
  hue?: Hue;
}

export function Accordion({ boxed, flush, size, hue, className, ...rest }: AccordionProps) {
  return (
    <div
      className={cx(
        'accordion',
        boxed && 'accordion--boxed',
        flush && 'accordion--flush',
        sizeClass('accordion', size),
        hue && `accordion--${hue}`,
        className,
      )}
      {...rest}
    />
  );
}

export interface AccordionItemProps extends Omit<DetailsHTMLAttributes<HTMLDetailsElement>, 'title'> {
  title: ReactNode;
  icon?: ReactNode;
  meta?: ReactNode;
  /** Items sharing a name open one at a time (native exclusive accordion). */
  name?: string;
  children: ReactNode;
}

/** One item: a native <details>, so it works with keyboard, screen readers and no JS. */
export function AccordionItem({ title, icon, meta, children, className, ...rest }: AccordionItemProps) {
  return (
    <details className={cx('accordion__item', className)} {...rest}>
      <summary className="accordion__summary">
        {icon && <span className="accordion__icon">{icon}</span>}
        <span>{title}</span>
        {meta && <span className="accordion__meta">{meta}</span>}
      </summary>
      <div className="accordion__content">{children}</div>
    </details>
  );
}
